import { useCallback, useEffect, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import type { SSODraft, SSOEndpoints, SSOSettings } from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState, StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { Mark, Masked, TestResult } from './connectParts'
import { Toggle, useFlash } from './sectionKit'
import { EDIT, InnerPage, Notice, PageHead, SettingBlock, SettingRow, SettingsCard, TagList, useOpenRecord, Values } from './settingsKit'
import '../styles/connect.css'

/**
 * Single sign-on: the organisation's OpenID Connect provider (Entra ID, ADFS,
 * Keycloak). The installation owner sets it; the organisation's administrator
 * reads it. English, as the owner's other connection screens are. Who a group
 * makes what is the Sign-in directory's group list, shared with LDAP.
 */

const BLANK: SSODraft = {
  displayName: '',
  issuer: '',
  clientId: '',
  scopes: 'openid profile email',
  usernameClaim: 'preferred_username',
  nameClaim: 'name',
  emailClaim: 'email',
  groupsClaim: 'groups',
  emailDomains: [],
  createAccounts: false,
  enabled: false,
}

const draftOf = (s: SSOSettings): SSODraft => ({
  displayName: s.displayName,
  issuer: s.issuer,
  clientId: s.clientId,
  scopes: s.scopes,
  usernameClaim: s.usernameClaim,
  nameClaim: s.nameClaim,
  emailClaim: s.emailClaim,
  groupsClaim: s.groupsClaim,
  emailDomains: s.emailDomains ?? [],
  createAccounts: s.createAccounts,
  enabled: s.enabled,
})

const refusal = (err: unknown) => (err instanceof ApiError ? err.message : 'The server could not be reached.')

export function SingleSignOn({ organisation, canConnect }: { organisation?: string; canConnect: boolean }) {
  const { say } = useStaffText()
  const { flash, toast } = useFlash()
  const [saved, setSaved] = useState<SSOSettings | null>(null)
  const [redirectUri, setRedirectUri] = useState('')
  const [centralOnly, setCentralOnly] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const rec = useOpenRecord([], () => '', loaded, canConnect && !centralOnly ? [EDIT] : [])
  const editing = canConnect && !centralOnly && rec.code === EDIT && loaded

  const load = useCallback(async () => {
    try {
      const r = await api.sso(organisation)
      setSaved(r.sso)
      setRedirectUri(r.redirectUri)
      setCentralOnly(r.centralOnly)
      setLoaded(true)
    } catch (err) {
      setError(refusal(err))
    }
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  const head = (
    <PageHead
      title={say('admin.section.sso')}
      line="Staff sign in with your identity provider."
      actions={
        loaded &&
        canConnect &&
        !centralOnly && (
          <Button variant={saved ? 'secondary' : 'primary'} onPress={() => rec.nav.open(EDIT)}>
            {saved ? (
              <>
                <Icon name="edit" size={15} /> Edit
              </>
            ) : (
              'Set up'
            )}
          </Button>
        )
      }
    />
  )

  if (editing) {
    return (
      <>
        <ProviderPage
          organisation={organisation}
          saved={saved}
          redirectUri={redirectUri}
          onClose={rec.close}
          onSaved={async () => {
            await load()
            rec.close()
            flash('Single sign-on saved')
          }}
        />
        {toast}
      </>
    )
  }

  if (!loaded) {
    return (
      <>
        {head}
        {error && <Notice>{error}</Notice>}
      </>
    )
  }

  return (
    <>
      {head}
      {centralOnly && <Notice tone="accent">Set up at the central server.</Notice>}
      <SettingsCard
        title="Identity provider"
        aside={
          <>
            {!canConnect && (
              <span className="dir-note">
                <Mark name="lock" size={14} /> {say('connect.ownerOnly')}
              </span>
            )}
            <StatusChip tone={saved?.enabled ? 'ok' : undefined}>{!saved ? 'Not set up' : saved.enabled ? 'On' : 'Off'}</StatusChip>
          </>
        }
      >
        {!saved && <EmptyState icon={<Icon name="key" size={20} />} title="No provider yet" />}
        {saved?.clientSecretUnreadable && (
          <SettingBlock>
            <Notice tone="warning">Client secret unreadable on this server. Enter it again.</Notice>
          </SettingBlock>
        )}
        {saved && (
          <>
            <SettingRow label="Button" description="On the sign-in page.">
              Sign in with {saved.displayName}
            </SettingRow>
            <SettingRow label="Provider">
              <Values
                items={[
                  { label: 'Issuer', value: <span className="connect-mono">{saved.issuer}</span> },
                  { label: 'Client ID', value: <span className="connect-mono">{saved.clientId}</span> },
                  { label: 'Client secret', value: saved.clientSecretSet ? <Masked /> : <span className="set-muted">None (public client)</span> },
                  { label: 'Scopes', value: <span className="connect-mono">{saved.scopes}</span> },
                ]}
              />
            </SettingRow>
            <SettingRow label="Claims">
              <Values
                items={[
                  { label: 'Username', value: <span className="connect-mono">{saved.usernameClaim}</span> },
                  { label: 'Name', value: <span className="connect-mono">{saved.nameClaim}</span> },
                  { label: 'Email', value: <span className="connect-mono">{saved.emailClaim}</span> },
                  {
                    label: 'Groups',
                    value: saved.groupsClaim ? <span className="connect-mono">{saved.groupsClaim}</span> : <span className="set-muted">Roles set by hand</span>,
                  },
                ]}
              />
            </SettingRow>
            <SettingRow label="Who may sign in">
              <Values
                items={[
                  { label: 'First sign-in', value: saved.createAccounts ? say('connect.createsAccount') : say('connect.existingOnly') },
                  {
                    label: 'Email domains',
                    value: (saved.emailDomains ?? []).length > 0 ? (saved.emailDomains ?? []).map((d) => `@${d}`).join(', ') : 'Any',
                  },
                ]}
              />
            </SettingRow>
          </>
        )}
        <SettingRow label="Redirect URI" description="Register it at the provider.">
          <span className="connect-mono">{redirectUri}</span>
        </SettingRow>
      </SettingsCard>

      {saved && canConnect && !centralOnly && <Discovery organisation={organisation} />}
      {toast}
    </>
  )
}

function ProviderPage({
  organisation,
  saved,
  redirectUri,
  onClose,
  onSaved,
}: {
  organisation?: string
  saved: SSOSettings | null
  redirectUri: string
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const { say } = useStaffText()
  const [draft, setDraft] = useState<SSODraft>(saved ? draftOf(saved) : BLANK)
  const [secret, setSecret] = useState('')
  const [domain, setDomain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (patch: Partial<SSODraft>) => setDraft((d) => ({ ...d, ...patch }))

  // The server refuses to keep a secret across a new issuer or client.
  const secretAgain = Boolean(saved?.clientSecretSet) && (draft.issuer.trim() !== saved?.issuer || draft.clientId.trim() !== saved?.clientId)
  const valid = draft.displayName.trim() !== '' && draft.issuer.trim() !== '' && draft.clientId.trim() !== '' && (!secretAgain || secret !== '')

  const addDomain = () => {
    const typed = domain
      .toLowerCase()
      .split(/[\s,]+/)
      .map((d) => d.replace(/^@/, ''))
      .filter(Boolean)
    set({ emailDomains: [...draft.emailDomains, ...typed.filter((d) => !draft.emailDomains.includes(d))] })
    setDomain('')
  }

  async function save() {
    if (busy || !valid) return
    setBusy(true)
    setError('')
    try {
      await api.saveSSO({
        organisation,
        ...draft,
        displayName: draft.displayName.trim(),
        issuer: draft.issuer.trim(),
        clientId: draft.clientId.trim(),
        // Absent keeps the stored secret; only a typed one replaces it.
        ...(secret ? { clientSecret: secret } : {}),
      })
      await onSaved()
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      back={say('admin.section.sso')}
      title={saved ? 'Identity provider' : 'Set up single sign-on'}
      onClose={onClose}
      onSubmit={() => void save()}
      submitLabel="Save"
      valid={valid}
      busy={busy}
    >
      {error && <Notice>{error}</Notice>}
      <Toggle title="Single sign-on" checked={draft.enabled} onChange={(enabled) => set({ enabled })} />

      <section className="connect-part">
        <h3 className="connect-part__title">Provider</h3>
        <label className="field">
          <span>Button name</span>
          <Input fullWidth maxLength={40} value={draft.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder="Bank login" />
        </label>
        <label className="field">
          <span>Issuer</span>
          <Input
            fullWidth
            className="connect-mono"
            value={draft.issuer}
            onChange={(e) => set({ issuer: e.target.value })}
            placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
          />
        </label>
        <label className="field">
          <span>Client ID</span>
          <Input fullWidth className="connect-mono" value={draft.clientId} onChange={(e) => set({ clientId: e.target.value })} />
        </label>
        <label className="field">
          <span>Client secret</span>
          <Input
            fullWidth
            type="password"
            autoComplete="new-password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={saved?.clientSecretSet && !secretAgain ? 'Stored. Leave empty to keep it.' : 'Empty for a public client'}
            aria-invalid={secretAgain && secret === ''}
          />
          {secretAgain && <small>Required: the issuer or client changed.</small>}
        </label>
        <label className="field">
          <span>Scopes</span>
          <Input fullWidth className="connect-mono" value={draft.scopes} onChange={(e) => set({ scopes: e.target.value })} />
        </label>
        <p className="set-muted">
          Redirect URI <span className="connect-mono">{redirectUri}</span>
        </p>
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Claims</h3>
        <div className="pk-fields">
          <label className="field">
            <span>Username</span>
            <Input fullWidth className="connect-mono" value={draft.usernameClaim} onChange={(e) => set({ usernameClaim: e.target.value })} />
          </label>
          <label className="field">
            <span>Name</span>
            <Input fullWidth className="connect-mono" value={draft.nameClaim} onChange={(e) => set({ nameClaim: e.target.value })} />
          </label>
          <label className="field">
            <span>Email</span>
            <Input fullWidth className="connect-mono" value={draft.emailClaim} onChange={(e) => set({ emailClaim: e.target.value })} />
          </label>
          <label className="field">
            <span title="Matched against the Sign-in directory's groups. Empty: roles set by hand.">Groups</span>
            <Input fullWidth className="connect-mono" value={draft.groupsClaim} onChange={(e) => set({ groupsClaim: e.target.value })} placeholder="groups" />
          </label>
        </div>
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Who may sign in</h3>
        <Toggle
          title="Create accounts"
          hint="On first sign-in, from their groups. Counted against the licence."
          checked={draft.createAccounts}
          onChange={(createAccounts) => set({ createAccounts })}
        />
        <TagList
          label="Email domains"
          items={draft.emailDomains.map((d) => ({ id: d, label: `@${d}` }))}
          onRemove={(d) => set({ emailDomains: draft.emailDomains.filter((x) => x !== d) })}
        />
        <div className="connect-chip-add">
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="bank.az"
            aria-label="Add an email domain"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                addDomain()
              }
            }}
          />
          <Button variant="secondary" isDisabled={!domain.trim()} onPress={addDomain}>
            <Icon name="plus" size={14} /> Add
          </Button>
        </div>
      </section>
    </InnerPage>
  )
}

/** Test: fetches the provider's discovery document now and shows what it names. */
function Discovery({ organisation }: { organisation?: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [found, setFound] = useState<SSOEndpoints | null>(null)

  async function run() {
    setBusy(true)
    setError('')
    setFound(null)
    try {
      setFound((await api.testSSO(organisation)).endpoints)
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard title="Test" line="Reads the provider's discovery document.">
      <SettingRow label="Discovery">
        <Button variant="primary" isDisabled={busy} onPress={() => void run()}>
          {busy ? 'Testing…' : 'Test'}
        </Button>
      </SettingRow>
      {(error || found) && (
        <SettingBlock>
          {error && (
            <TestResult ok={false} title="Not reached">
              <span>{error}</span>
            </TestResult>
          )}
          {found && (
            <TestResult ok title="Provider found">
              <Values
                items={[
                  { label: 'Issuer', value: <span className="connect-mono">{found.issuer}</span> },
                  { label: 'Sign-in', value: <span className="connect-mono">{found.authorizationEndpoint}</span> },
                  { label: 'Token', value: <span className="connect-mono">{found.tokenEndpoint}</span> },
                  { label: 'Keys', value: <span className="connect-mono">{found.jwksUri}</span> },
                  {
                    label: 'Sign-out',
                    value: found.endSessionEndpoint ? <span className="connect-mono">{found.endSessionEndpoint}</span> : <span className="set-muted">None</span>,
                  },
                  { label: 'Algorithms', value: found.algorithms.join(', ') },
                ]}
              />
            </TestResult>
          )}
        </SettingBlock>
      )}
    </SettingsCard>
  )
}
